import React, { useEffect, useState } from "react";

import editIcon from "../../../../../../../../Assets/images/edit-icon.svg";
import deleteIcon from "../../../../../../../../Assets/images/delete-icon.svg";

import AssignModal from "../../../../JobBoardItems/AssignModal/AssignModal";
import AdjustTons from "../AdjustTons/AdjustTons";
import TruckDetails from "../TruckDetails/TruckDetails";
import "./TruckLists.scss";
import ConfirmationModal from "../../../../../../../../Components/ConfirmationBox/ConfirmationModal";
import apiClient from "../../../../../../../../Config/apiClient";
import { ClipLoader } from "react-spinners";
import { showErrorToast, showSuccessToast } from "../../../../../../../../Context/Toaster/Toaster";
import { DATE_FORMAT } from "../../../../../Utils/Constants";

import moment from "moment";
import { jwtDecode } from "jwt-decode";

const TruckUrl = process.env.REACT_APP_TC_TRUCK_BASE_PATH;

const TrucksTab = ({ jobId, refresh, jobData, triggerParentRefresh }) => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTruckDetails, setShowTruckDetails] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showAdjustTons, setShowAdjustTons] = useState(false);
  const [selectedTruckJob, setSelectedTruckJob] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role);
      } catch {
        setUserRole(null);
      }
    }

    if (jobId) fetchTrucks();
  }, [jobId, refresh]);

  const handleOpenModal = (job) => {
    setSelectedJob(job);
    setShowAssignModal(true);
  };

  const handleCloseModal = () => {
    setShowAssignModal(false);
    setSelectedJob(null);
    fetchTrucks();
  };

  const handleDeleteClick = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAssignment?.id) return;

    try {
      setLoading(true);
      const response = await apiClient.delete(`${TruckUrl}/unassign`, {
        data: { assignment_id: selectedAssignment.id },
      });

      if (response.data.status === "SUCCESS") {
        showSuccessToast("Truck and driver unassigned successfully!");
        fetchTrucks();

        if (typeof triggerParentRefresh === "function") {
          triggerParentRefresh();
        }
      } else {
        showErrorToast(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.message;
      showErrorToast(message);
    } finally {
      setShowDeleteModal(false);
      setSelectedAssignment(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) fetchTrucks();
  }, [jobId, refresh]);

  const getCompanyId = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.company_id;
    } catch {
      return null;
    }
  };

  const fetchTrucks = async () => {
    try {
      setLoading(true);
      if (!TruckUrl) throw new Error("TruckUrl is undefined");
      const companyId = getCompanyId();
      if (!companyId) throw new Error("Company ID not found in token");

      const response = await apiClient.get(
        `${TruckUrl}/trucks/${jobId}`,
        { params: { tc_company_id: companyId } }
      );
      const data = response.data?.data || [];
      setTrucks(data);
    } catch (error) {
      console.error("Error fetching trucks:", error);
      showErrorToast("Failed to load trucks");
    } finally {
      setLoading(false);
    }
  };

  const openTruckDetails = async (e, truck) => {
    e.preventDefault();
    const truckId = truck?.truckDetails?.truck_id;
    if (!truckId) return showErrorToast("Truck ID not found");

    try {
      setLoading(true);
      const response = await apiClient.get(`${TruckUrl}/details/${truckId}`);
      const detailedTruck = response.data?.data;

      if (!detailedTruck) return showErrorToast("Truck details not found");

      setSelectedTruck({
        ...detailedTruck,
        driverDetails: truck.driverDetails,
        driver: `${truck.driverDetails?.first_name || ""} ${truck.driverDetails?.last_name || ""}`.trim()
      });

      setShowTruckDetails(true);
    } catch (error) {
      console.error("Error fetching truck details:", error);
      showErrorToast("Failed to fetch truck details.");
    } finally {
      setLoading(false);
    }
  };

  const closeTruckDetails = () => {
    setShowTruckDetails(false);
    setSelectedTruck(null);
  };

  const getUnitLabel = (paymentType) => {
    if (paymentType === 2 || paymentType === "Hours" || paymentType === "Hour") return "Hours";
    if (paymentType === 3 || paymentType === "Loads" || paymentType === "Load") return "Loads";
    return "Tons";
  };

  return (
    <>
      <div className="tr-inner">
        <div className="trt-wraper" style={{ position: "relative" }}>
          <table className="table trucks-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Truck ID</th>
                <th>Driver</th>
                <th>Job Accepted</th>
                <th className="align-text">Work Done</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody style={{ opacity: loading ? 0.4 : 1, pointerEvents: loading ? "none" : "auto" }}>
              {trucks.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    No trucks found for this job.
                  </td>
                </tr>
              ) : (
                trucks.map((truck, index) => {
                  const truckId = truck.truckDetails?.truck_id;
                  const driverName = `${truck.driverDetails?.first_name || ""} ${truck.driverDetails?.last_name || ""}`.trim();
                  const jobAccepted = moment(truck.applied_datetime).local().format(DATE_FORMAT);
                  const tonsDumped = truck.work_done;

                  return (
                    <tr key={truck.id}>
                      <td>{index + 1}</td>
                      <td>
                        <a href="#" onClick={(e) => openTruckDetails(e, truck)}>
                          T-{truckId || "--"}
                        </a>
                      </td>
                      <td>
                        <a href="#" onClick={(e) => openTruckDetails(e, truck)}>
                          {driverName || "--"}
                        </a>
                      </td>
                      <td>{jobAccepted}</td>
                      <td className="align-text">
                        {tonsDumped != null && !isNaN(tonsDumped)
                          ? `${Number(tonsDumped).toFixed(2)} ${getUnitLabel(jobData?.payment_type)}`
                          : "--"}
                      </td>
                      <td className="edit-col">
                        {userRole !== "Truck-Driver" && (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();

                              setSelectedJob({
                                ...jobData,
                                assignment_id: truck.id,
                                truck_id: truck.truckDetails?.truck_id,
                                driver_id: truck.driverDetails?.id,
                                mode: "edit",
                              });

                              setShowAssignModal(true);
                            }}
                            className={
                              ["In Progress", "Cancelled", "Completed"].includes(jobData?.job_status)
                                ? "disabled-link"
                                : ""
                            }
                            style={{
                              pointerEvents: ["In Progress", "Cancelled", "Completed"].includes(
                                jobData?.job_status
                              )
                                ? "none"
                                : "auto",
                              opacity: ["In Progress", "Cancelled", "Completed"].includes(
                                jobData?.job_status
                              )
                                ? 0.5
                                : 1,
                            }}
                          >
                            <img
                              className="tb-icon"
                              src={editIcon}
                              alt="Edit"
                            />
                          </a>
                        )}
                      </td>

                      <td>
                        {userRole !== "Truck-Driver" && (
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDeleteClick(truck);
                            }}
                            className={
                              ["In Progress", "Cancelled", "Completed"].includes(jobData?.job_status)
                                ? "disabled-link"
                                : ""
                            }
                            style={{
                              pointerEvents: ["In Progress", "Cancelled", "Completed"].includes(
                                jobData?.job_status
                              )
                                ? "none"
                                : "auto",
                              opacity: ["In Progress", "Cancelled", "Completed"].includes(
                                jobData?.job_status
                              )
                                ? 0.5
                                : 1,
                            }}
                          >
                            <img
                              className="ct-icon"
                              src={deleteIcon}
                              alt="Delete"
                            />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {loading && (
            <div style={{ position: "absolute", top: "40px", left: 0, width: "100%", height: "calc(100% - 40px)", background: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, }}>
              <ClipLoader size={35} />
            </div>
          )}
        </div>
      </div>

      <AdjustTons
        show={showAdjustTons}
        onClose={() => {
          setShowAdjustTons(false);
          setSelectedTruckJob(null);
          refresh();
        }}
        truckJobId={selectedTruckJob?.id}
        jobData={jobData}
        truckDetails={{
          truckId: selectedTruckJob?.truckDetails?.truck_id,
          licensePlate: selectedTruckJob?.truckDetails?.license_plate_no,
          vehicleNumber: selectedTruckJob?.truckDetails?.vehicle_no,
          truckType: selectedTruckJob?.truckDetails?.truck_type,
        }}
        initialWorkDone={selectedTruckJob?.work_done}
      />

      {showAssignModal && (
        <AssignModal job={selectedJob} onClose={handleCloseModal} />
      )}

      <TruckDetails truck={selectedTruck} show={showTruckDetails} onClose={closeTruckDetails} />

      {showDeleteModal && (
        <ConfirmationModal
          show={showDeleteModal}
          title="Confirm Unassign"
          message="Are you sure you want to unassign this truck and driver from the job?"
          confirmText="Yes"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

    </>
  );
};

export default TrucksTab;
